/**
 * Shared page frame for a stepper page: fill the dashboard content area, header
 * pinned at the top, one scrolling centered column, optional sticky footer.
 *
 * Extracted from ChartMindPage, which is the origin of these values — the
 * 100dvh-minus-AppBar height, the 700px column, and the xs safe-area top padding
 * all came from there. CareStartPage uses the same height rule.
 *
 * The 56px in that height is the dashboard's mobile AppBar. On a public route
 * there is no AppBar, so it reads as 56px of dead space at the bottom — pass
 * `standalone` there. CareStartPage carries the same distinction as `embedded`,
 * from the other side (see CareStartPage's minHeight).
 */

import React from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { DASHBOARD_CONTENT_HEIGHT } from "constants/layout";

export default function StepperPageShell({
  header = null,
  footer = null,
  children,
  // Full width by default. A page that wants a reading column renders its own
  // inner Box, the way ChartMindPage does — that is a content decision, not the
  // wrapper's, and keeping it out of here is what makes the wrapper identical
  // across all three pages.
  maxWidth = false,
  // True when this page owns the whole viewport rather than the dashboard's
  // content area — i.e. a public route, outside DashboardLayout.
  standalone = false,
  ...rest
}) {
  return (
    <Box
      sx={{
        // The dashboard's <main> reserves room for its top bar; a public route
        // has no bar to make room for.
        height: standalone ? "100dvh" : DASHBOARD_CONTENT_HEIGHT,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        overflow: "hidden",
      }}
      {...rest}
    >
      {header}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          px: { xs: 2, sm: 3 },
          pt: { xs: "calc(20px + env(safe-area-inset-top, 0px))", sm: 4 },
          pb: 4,
          overflowY: "auto",
        }}
      >
        <Box sx={{ width: "100%", ...(maxWidth ? { maxWidth } : {}) }}>
          {children}
        </Box>
      </Box>
      {footer}
    </Box>
  );
}

StepperPageShell.propTypes = {
  header: PropTypes.node,
  footer: PropTypes.node,
  children: PropTypes.node,
  /** Optional reading-column cap. Omit for full width. */
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  /** Set on a public route, which has no dashboard AppBar to reserve height for. */
  standalone: PropTypes.bool,
};
